import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Star, Archive } from 'lucide-react';
import { api } from '@/lib/api-client';
import { useLinks } from '@/hooks/useLinks';
import { LinkCard } from './LinkCard';
import { LinkCardSkeleton } from './LinkCardSkeleton';
import { EmptyState } from './EmptyState';
import { SwipeableCard } from './SwipeableCard';
import type { SortField, SortOrder } from '@/hooks/useSortPreference';
import type { LinkResponse, ListLinksResponse } from '@readlater/shared';

interface LinksListProps {
  filter?: 'unread' | 'favorites' | 'archived';
  sortBy?: SortField;
  sortOrder?: SortOrder;
}

export function LinksList({ filter, sortBy = 'created_at', sortOrder = 'desc' }: LinksListProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useLinks({ filter });

  // Mutation for updating link (favorite/archive/read)
  const updateLink = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: { is_favorite?: boolean; is_archived?: boolean; is_read?: boolean };
    }) => {
      return api.patch<LinkResponse>(`/links/${id}`, updates);
    },
    onMutate: async ({ id, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['links'] });

      // Snapshot previous values for all link queries
      const previousAll = queryClient.getQueryData<ListLinksResponse>(['links', 'all']);
      const previousUnread = queryClient.getQueryData<ListLinksResponse>(['links', 'unread']);
      const previousFavorites = queryClient.getQueryData<ListLinksResponse>(['links', 'favorites']);
      const previousArchived = queryClient.getQueryData<ListLinksResponse>(['links', 'archived']);

      // Helper to update links in a query
      const updateLinksInQuery = (key: string[]) => {
        queryClient.setQueryData<ListLinksResponse>(key, (old) => {
          if (!old) return old;
          return {
            ...old,
            links: old.links.map((link) => (link.id === id ? { ...link, ...updates } : link)),
          };
        });
      };

      // Update all relevant queries
      updateLinksInQuery(['links', 'all']);
      updateLinksInQuery(['links', 'unread']);
      updateLinksInQuery(['links', 'favorites']);
      updateLinksInQuery(['links', 'archived']);

      return { previousAll, previousUnread, previousFavorites, previousArchived };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousAll) {
        queryClient.setQueryData(['links', 'all'], context.previousAll);
      }
      if (context?.previousUnread) {
        queryClient.setQueryData(['links', 'unread'], context.previousUnread);
      }
      if (context?.previousFavorites) {
        queryClient.setQueryData(['links', 'favorites'], context.previousFavorites);
      }
      if (context?.previousArchived) {
        queryClient.setQueryData(['links', 'archived'], context.previousArchived);
      }
      toast.error('Failed to update link');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['links'] });
    },
  });

  const handleToggleFavorite = (id: number) => {
    const link = data?.links.find((l) => l.id === id);
    if (link) {
      updateLink.mutate({ id, updates: { is_favorite: !link.is_favorite } });
      toast.success(link.is_favorite ? 'Removed from favorites' : 'Added to favorites');
    }
  };

  const handleToggleArchive = (id: number) => {
    const link = data?.links.find((l) => l.id === id);
    if (link) {
      updateLink.mutate({ id, updates: { is_archived: !link.is_archived } });
      toast.success(link.is_archived ? 'Unarchived' : 'Archived');
    }
  };

  const handleToggleRead = (id: number) => {
    const link = data?.links.find((l) => l.id === id);
    if (link) {
      updateLink.mutate({ id, updates: { is_read: !link.is_read } });
      toast.success(link.is_read ? 'Marked as unread' : 'Marked as read');
    }
  };

  // Mutation for deleting link
  const deleteLink = useMutation({
    mutationFn: async (id: number) => {
      return api.delete(`/links/${id}`);
    },
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: ['links'] });

      const previousAll = queryClient.getQueryData<ListLinksResponse>(['links', 'all']);
      const previousUnread = queryClient.getQueryData<ListLinksResponse>(['links', 'unread']);
      const previousFavorites = queryClient.getQueryData<ListLinksResponse>(['links', 'favorites']);
      const previousArchived = queryClient.getQueryData<ListLinksResponse>(['links', 'archived']);

      const removeFromQuery = (key: string[]) => {
        queryClient.setQueryData<ListLinksResponse>(key, (old) => {
          if (!old) return old;
          return {
            ...old,
            links: old.links.filter((link) => link.id !== id),
          };
        });
      };

      removeFromQuery(['links', 'all']);
      removeFromQuery(['links', 'unread']);
      removeFromQuery(['links', 'favorites']);
      removeFromQuery(['links', 'archived']);

      return { previousAll, previousUnread, previousFavorites, previousArchived };
    },
    onError: (_err, _id, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(['links', 'all'], context.previousAll);
      }
      if (context?.previousUnread) {
        queryClient.setQueryData(['links', 'unread'], context.previousUnread);
      }
      if (context?.previousFavorites) {
        queryClient.setQueryData(['links', 'favorites'], context.previousFavorites);
      }
      if (context?.previousArchived) {
        queryClient.setQueryData(['links', 'archived'], context.previousArchived);
      }
      toast.error('Failed to delete link');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
    },
  });

  const handleDelete = (id: number) => {
    deleteLink.mutate(id);
    toast.success('Link deleted');
  };

  // Sort links
  const sortedLinks = useMemo(() => {
    if (!data?.links) return [];

    return [...data.links].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'title':
          comparison = (a.title || a.url).localeCompare(b.title || b.url);
          break;
        case 'reading_time':
          comparison = (a.reading_time || 0) - (b.reading_time || 0);
          break;
        case 'created_at':
        default:
          comparison =
            new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [data?.links, sortBy, sortOrder]);

  // Loading state
  if (isLoading) {
    return (
      <div>
        {Array.from({ length: 5 }).map((_, i) => (
          <LinkCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Error state
  if (isError) {
    return <EmptyState type="error" error={error} onRetry={() => refetch()} />;
  }

  // Empty state (with ?empty=true override for testing)
  const searchParams = new URLSearchParams(window.location.search);
  const forceEmpty = searchParams.get('empty') === 'true';
  if (forceEmpty || !data?.links?.length) {
    return <EmptyState type={filter || 'all'} />;
  }

  // Links list
  return (
    <div>
      {sortedLinks.map((link) => (
        <SwipeableCard
          key={link.id}
          onSwipeRight={() => handleToggleFavorite(link.id)}
          onSwipeLeft={() => handleToggleArchive(link.id)}
          leftAction={{
            icon: Star,
            label: link.is_favorite ? 'Unfavorite' : 'Favorite',
            color: 'text-yellow-600',
            bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
          }}
          rightAction={{
            icon: Archive,
            label: link.is_archived ? 'Unarchive' : 'Archive',
            color: 'text-blue-600',
            bgColor: 'bg-blue-100 dark:bg-blue-900/30',
          }}
        >
          <LinkCard
            link={link}
            onToggleFavorite={handleToggleFavorite}
            onToggleArchive={handleToggleArchive}
            onToggleRead={handleToggleRead}
            onDelete={handleDelete}
          />
        </SwipeableCard>
      ))}
    </div>
  );
}

// Export the link count for use in ListHeader
export function useLinksCount(filter?: 'unread' | 'favorites' | 'archived') {
  const { data, isLoading } = useLinks({ filter });
  return { count: data?.links?.length ?? 0, isLoading };
}

// Export all filter counts computed from cached "all" query (no extra API calls)
export interface FilterCounts {
  all: number;
  unread: number;
  favorites: number;
  archived: number;
}

export function useAllLinksCounts(): { counts: FilterCounts; isLoading: boolean } {
  // Reuse the cached 'all' query - no filter means we get all links
  const { data, isLoading } = useLinks();

  const counts = useMemo<FilterCounts>(() => {
    if (!data?.links) return { all: 0, unread: 0, favorites: 0, archived: 0 };

    const links = data.links;
    return {
      all: links.length,
      unread: links.filter((l) => !l.is_read && !l.is_archived).length,
      favorites: links.filter((l) => l.is_favorite).length,
      archived: links.filter((l) => l.is_archived).length,
    };
  }, [data?.links]);

  return { counts, isLoading };
}
